import React from "react";

import { useService } from "@xstate/react";
import styled from "styled-components";

import { productBuilderMachine } from "../../../context/ProductBuilder/machine";
import {
  getColourFromColourTag,
  getColourOptions,
} from "../../../helpers/colour";
import { getBaseProductType } from "../../../helpers/getBaseProductType";
import { isCurrentState } from "../../../helpers/isCurrentState";
import {
  productHasVariantWithMaterialType,
  productHasVariantWithTransparencyType,
} from "../../../helpers/material";
import { throwError } from "../../../helpers/throwError";
import { BaseProduct, ProductBuilderMachineEvent } from "../../../types";
import { Heading } from "../../Common/Heading";
import { Selector, SelectorColour } from "../../Common/Selector";
import { AddFreeSample } from "./AddFreeSample";

const hexSorter = require("hexsorter");

interface Color {
  name: string;
  code: string;
  "25mm_swatch_url": string;
  "45mm_swatch_url": string;
  featured_image: string;
  colour_discount_code: string;
}

interface ColorDiscountCodes {
  [discount: string]: Color[];
}

type OnClick = (
  colour: string,
  send: (event: ProductBuilderMachineEvent) => void,
  materialType: string,
  products: BaseProduct[],
  discountCode?: string
) => () => void;

const onClick: OnClick = (colour, send, materialType, products, discountCode) => () => {
  let rollerPremiumCorded =
    products &&
    products.filter(
      (ele) =>
        ele.title
          ?.toLowerCase()
          ?.includes(`Material: ${materialType}`?.toLowerCase()) &&
        ele.title
          ?.toLowerCase()
          ?.replace("-", " ")
          ?.includes(`${colour}`?.toLowerCase()) &&
        ele?.metafields?.supplier != undefined
    );
  let getColourDiscoutCode = discountCode ? null : findProduct(materialType, colour, products);
  let rawDiscountCode = discountCode || getColourDiscoutCode?.metafields?.colour_discount_code?.[0] || "";
  // Clean discount code - remove "%" if present and ensure it's a valid number string
  const finalDiscountCode = rawDiscountCode.toString().replace("%", "").trim();
  if (
    window.location.pathname?.includes("/roller-premium") ||
    window.location.pathname?.includes("/roller-single") ||
    window.location.pathname?.includes("/roller-blind-essentials")
  ) {
    send({
      type: "UPDATE_CUSTOM",
      custom: {
        colour,
        bracketColour:
          rollerPremiumCorded?.[0]?.metafields?.bracket_colour?.[0],
        componentsColour:
          rollerPremiumCorded?.[0]?.metafields?.components_colour?.[0],
        chainColour:
          rollerPremiumCorded?.[0]?.metafields?.chain_colour?.[0],
        bottomBarColour:
          rollerPremiumCorded?.[0]?.metafields?.bottom_bar_colour?.[0],
        colourDiscoutCode: finalDiscountCode,
      },
    });
  } else {
    send({
      type: "UPDATE_CUSTOM",
      custom: {
        colour,
        colourDiscoutCode: finalDiscountCode,
      },
    });
  }
};

function findProduct(
  materialType: string,
  colour: string,
  products: BaseProduct[]
) {
  // First try to find by materialType (for products that use materialType)
  let found = products.find((product) => {
    return (
      product.tags.includes(`Variant (Fabric Type): ${materialType}`) &&
      product.tags.includes(colour)
    );
  });
  
  // If not found and materialType might not be used, try finding by colour only
  if (!found) {
    found = products.find((product) => {
      return product.tags.includes(colour);
    });
  }
  
  return found;
}

const Wrapper = styled.div`
  min-width: 300px;
  max-width: 100%;
  text-align: center;

  .keen-slider-selector__slide {
    min-width: 34px !important;
    max-width: 34px !important;
  }

  @media (min-width: 1441px) {
    padding: 0px 52px;
    width: 100%;
  }

  @media (max-width: 1440px) {
    padding: 0px 25px;
  }

  @media (max-width: 767px) {
    .keen-slider-selector__slide {
      margin: 0px;
      min-width: 34px !important;
      max-width: 34px !important;
    }
  }
`;

const Caption = styled.div`
  text-align: center;
  font: 300 14px / 20px Lato;
  letter-spacing: 0.28px;
  color: rgb(38, 55, 95);
  max-width: 100%;
  margin: 7px auto 7px;
`;

const SaleWrapper = styled.span`
  // "@media (min-width: 768px)": {
  font-size: 10px;
  color: #fff;
  background: #ff007f;
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  // zIndex: 4;
  position: relative;
  border-radius: 50%;
  transform: rotate(-40deg);
  margin-right: 2px;
  // }
`;

const DiscountLabel = styled("div")({
  "@media (min-width: 768px)": {
    // padding: "0 40px",
    // marginTop: "10px",
  },
});

export const Colour = () => {
  const [state, send] = useService(productBuilderMachine);
  const productType = getBaseProductType(state.context);

  const blindType =
    typeof productType === "string" ? productType.toLowerCase() : "blind";

  const captionProductType: string = blindType.includes("shutter")
    ? "shutter"
    : blindType.includes("curtain")
    ? "curtain"
    : "blind";

  const {
    products,
    custom: { materialType, colour, transparency },
  } = state.context;

  const productTypes = Array.from(
    new Set(
      products.map((product) => {
        return product.type;
      })
    )
  );

  const productTypesWithoutMaterialType = [
    "Tecwood Plantation Shutter",
    "Venetian PVC Corded",
    "Venetian PVC Cordless",
    "Venetian Timber",
    "Venetian Timber Corded",
    "Venetian Timber Cordless",
    "Venetian Aluminium",
    "Roman Blinds Chain",
    "Roman Blinds Chainless",
    "Curtain",
    "Sheer Curtain",
  ];

  const useProductWithVariantMaterialType = !productTypesWithoutMaterialType.includes(
    productTypes[0]
  );

  const colourOptions: {
    name: string;
    code: string;
    featured_image: string;
    "25mm_swatch_url": string;
    "45mm_swatch_url": string;
    colour_discount_code: string;
  }[] = getColourOptions(
    useProductWithVariantMaterialType
      ? products.filter(productHasVariantWithMaterialType(materialType))
      : productTypes[0] == "Curtain"
      ? products.filter(productHasVariantWithTransparencyType(transparency))
      : products
  );

  let colorDiscountCodes: ColorDiscountCodes = {};

  colourOptions.forEach((item) => {
    const discountCode = item.colour_discount_code;
    if (!colorDiscountCodes[discountCode]) {
      colorDiscountCodes[discountCode] = [];
    }
    colorDiscountCodes[discountCode].push({
      ...item,
    });
  });
  const sortedColorDiscountCodes = Object.entries(colorDiscountCodes)
    .sort((a, b) => {
      const discountA = parseInt(a[0].toString().replace("%", "").trim());
      const discountB = parseInt(b[0].toString().replace("%", "").trim());
      return discountB - discountA;
    })
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {} as ColorDiscountCodes);

  let colourHexValues: string[] = colourOptions.map((colour) => colour.code);
  var mostBright = hexSorter.sortColors(colourHexValues, "mostBrightColor");

  let sortedOptions;
  /**
   * Sort array of objects based on another array
   */

  const mapOrder = (
    array: {
      name: string;
      code: string;
      featured_image: string;
      "25mm_swatch_url": string;
      "45mm_swatch_url": string;
    }[],
    order: string[],
    key: string
  ) => {
    array.sort((a: any, b: any) => {
      const A = a[key];
      const B = b[key];
      return order.indexOf(A) > order.indexOf(B) ? 1 : -1;
    });
    return array;
  };

  sortedOptions = mapOrder(colourOptions, mostBright, "code");

  if (isCurrentState("colour", state) && colourOptions.length === 0) {
    throwError(state);
  }

  return productTypes[0].includes("Honeycomb") ||
    productTypes[0].includes("Roller Blind") ||
    productTypes[0].includes("Plantation Shutter") ||
    productTypes[0].includes("Venetian") ||
    productTypes[0].includes("Roman Blinds") ||
    productTypes[0].includes("Curtain") ||
    productTypes[0].includes("Sheer Curtain") ? (
    <Wrapper>
      <Heading>Colour</Heading>
      <Caption>Choose a colour for your {captionProductType}</Caption>
      {Object.entries(sortedColorDiscountCodes).map(([discount, data]) => (
        <div
          key={discount}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            marginBlock: "10px",
          }}
        >
          {discount !== "0%" ? (
            <DiscountLabel>
              <SaleWrapper>SALE</SaleWrapper> {discount} Off
            </DiscountLabel>
          ) : null}
          <div style={{ marginTop: "10px" }} />
          <Selector
            isSlider={true}
            isColorPicker={true}
            isColorArrow={true}
            isHoneyComb={true}
          >
            {data &&
              data.map((item) => {
                const isSelected = item.name === colour;
                return (
                  <SelectorColour
                    isSelected={isSelected}
                    onClick={onClick(item.name, send, materialType, products, item.colour_discount_code)}
                    key={item.name}
                    colourName={item.name}
                    colourCode={item.code}
                    colourSwatch={item.featured_image}
                    colour_discount_code={item.colour_discount_code}
                    isHoneyComb={productTypes[0].includes("Honeycomb Standard")}
                  />
                );
              })}
          </Selector>
        </div>
      ))}
      <Caption>{getColourFromColourTag(colour || "")}</Caption>
      <AddFreeSample title={getColourFromColourTag(colour || "")} />
    </Wrapper>
  ) : (
    <Wrapper>
      <Heading>Colour</Heading>
      <Caption>Choose colour of your {captionProductType}</Caption>
      <Selector isSlider={true} isColorPicker={true} isColorArrow={true}>
        {sortedOptions.map((option) => {
          const isSelected = option.name === colour;
          return (
            <SelectorColour
              isSelected={isSelected}
              onClick={onClick(option.name, send, materialType, products, (option as any).colour_discount_code)}
              key={option.name}
              colourName={option.name}
              colourCode={option.code}
              colourSwatch={option.featured_image}
              isHoneyComb={productTypes[0].includes("Honeycomb Standard")}
            />
          );
        })}
      </Selector>

      <Caption>{getColourFromColourTag(colour || "")}</Caption>
      <AddFreeSample title={getColourFromColourTag(colour || "")} />
    </Wrapper>
  );
};
